import { inject, Injectable } from '@angular/core';
import { BillingUserContextService } from '@rma/authentication/public/user';
import { CouponClient, CouponModel, PriceModel } from '@rma/billing-api';
import { FeatureFlags, FeaturesService } from '@rma/generic/util/feature-flags';
import { getQueryStringParam } from '@rma/generic/util/http';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

const couponCodeKey = 'couponCode';

type CouponState = 'url' | 'default' | 'cookie' | 'none' | 'feature';

@Injectable({
  providedIn: 'root',
})
export class CouponService {
  private readonly billingUserContextService = inject(BillingUserContextService);
  private readonly cookiesService = inject(CookieService);
  private readonly couponClient = inject(CouponClient);
  private readonly featureService = inject(FeaturesService);
  private couponState: CouponState = 'feature';

  public initialiseCoupon() {
    this.couponState = 'feature';
  }

  public coupon(price?: PriceModel) {
    const coupon$ = this.couponState === 'feature' ? this.featureService.getFeature$(FeatureFlags.PromotionCouponCode, null) : of(null);
    return coupon$.pipe(
      switchMap((promotionCode) =>
        promotionCode
          ? this.couponClient.getCouponDetails(promotionCode).pipe(
              map((coupon: CouponModel | null) => coupon?.CouponCode),
              catchError((err) => {
                if (err.Status < 500) {
                  // expired or invalid coupons return 400, act as if it wasn't there.
                  return of(null);
                } else {
                  throw err;
                }
              }),
            )
          : of(null),
      ),
      map((promotionCode) => {
        if (promotionCode) {
          return promotionCode;
        } else if (this.couponState === 'feature') {
          this.couponState = 'url';
        }

        if (this.couponState === 'url') {
          const couponCode = getQueryStringParam(couponCodeKey);
          if (couponCode) {
            return couponCode;
          }
          this.couponState = 'cookie';
        }

        if (this.couponState === 'cookie') {
          const couponCode = this.cookiesService.get('RateMyAgentCheckoutCouponCookie');

          if (couponCode) {
            return couponCode;
          }
        }

        if (this.couponState !== 'none') {
          return price?.DefaultCoupon?.CouponCode;
        }

        return undefined;
      }),
    );
  }

  public tryAnotherCoupon(): boolean {
    if (this.couponState === 'feature') {
      this.couponState = 'url';
      return true;
    }
    if (this.couponState === 'url') {
      this.couponState = 'cookie';
      return true;
    }
    if (this.couponState === 'cookie') {
      this.couponState = 'default';
      return true;
    }
    if (this.couponState === 'default') {
      this.couponState = 'none';
      return true;
    }
    return false;
  }

  public getDefaultCoupon(): Observable<CouponModel | null> {
    return this.billingUserContextService.userContext$.pipe(
      switchMap((context) =>
        this.couponClient.getDefaultCouponForCustomer(context.productUserGroupingType, context.productUserType, context.code).pipe(
          catchError((error) => {
            if (error.Status === 404) {
              return of(null);
            }
            throw error;
          }),
        ),
      ),
    );
  }
}
